import { createContext, Suspense, useContext, useMemo, useState } from 'react';
import { RouteFallback } from '@/app/RouteFallback';
import { Link, Outlet, ScrollRestoration } from 'react-router-dom';
import { GovBar } from '@/features/common/layout/GovBar';
import { SITE_PAGE } from '@/features/common/layout/publicPageLinks';
import { PublicHeader } from '@/features/common/layout/PublicHeader';
import { SiteFooter } from '@/features/common/layout/SiteFooter';
import { LANGUAGE_FALLBACK } from '@/features/common/languageFallback';
import type { LanguageFallback } from '@/features/common/types/LanguageFallback';
import { publicLayout, type PublicLayoutKey } from '@/i18n/dictionaries/publicLayout';
import { useTranslation } from '@/i18n/useTranslation';
import { useDocumentTitle } from '@/features/common/useDocumentTitle';
import { PAGE_VIEW, usePageViewCounter } from '@/features/common/usePageViewCounter';
import '@/features/publicsite/publicsite.css';
import '@/app/shells/shell.css';
import '@/app/shells/public-shell.css';

/**
 * Whether the page below is showing Romanian content to a visitor who asked for English.
 *
 * Clarified FR-004: content is translated per item, so an English visitor can land on a page
 * that only exists in Romanian. The rule is to serve it anyway and say so, rather than show an
 * empty page — and the notice belongs above <main>, outside whatever page raised it, which is
 * why this is a context and not a prop. A page reads what its payload says about language and
 * calls setFallback. A listing whose items are translated one by one can be partly Romanian, and
 * saying the whole page is not in English there would be untrue (T144).
 */
const FallbackContext = createContext<(v: LanguageFallback) => void>(() => {});

const FALLBACK_NOTICE: Record<LanguageFallback, PublicLayoutKey | null> = {
  NONE: null,
  PARTIAL: 'langIndicatorPartial',
  WHOLE: 'langIndicatorFallback',
};

/**
 * What the page below wants `document.title` to say about itself.
 *
 * A context rather than each page calling useDocumentTitle for itself, because a page that did
 * lost the argument: React runs child effects before parent ones, so the page set its title and
 * the shell's own useDocumentTitle overwrote it on the very next line of the same commit. Every
 * list page ended up with the zone title; only the detail pages appeared to work, and only because
 * their title arrives in a later effect, after the shell had already had its turn. Reported
 * upward, the shell composes the one title there is and the ordering stops mattering.
 */
const PageTitleContext = createContext<(v: string | null) => void>(() => {});

/** Used by usePublicPageTitle; pages call that, not this. */
export function usePageTitleSetter() {
  return useContext(PageTitleContext);
}

/**
 * What `document.title` says, given what the page below reported.
 *
 * The page's own name comes FIRST: a browser tab, a bookmark and a search result all truncate
 * from the right, so the half that identifies the page is the half that has to survive. `null`
 * means the page named nothing — the home page, or one still loading its subject — and the zone
 * title stands in, which is descriptive on its own rather than a placeholder.
 *
 * The suffix is skipped where the name already carries the brand: "Biblioteca digitală AKIS" and
 * "Harta AKIS" are named that way on the page itself, and appending to them read "… AKIS — AKIS".
 *
 * Exported and pure so the rules above are testable without rendering the shell — this project's
 * Vitest setup has no DOM.
 */
export function publicDocumentTitle(
  pageTitle: string | null,
  suffix: string,
  appTitle: string,
): string {
  if (!pageTitle) return appTitle;
  // endsWith, not includes: a title that merely mentions the brand mid-sentence ("Lansarea
  // sistemului AKIS în 12 raioane") still needs the suffix — only one that is already named
  // with it at the end ("Harta AKIS") does not.
  return pageTitle.endsWith(suffix) ? pageTitle : `${pageTitle} — ${suffix}`;
}

/** Call with what the payload a page just loaded says about its language. */
export function useLanguageFallback() {
  return useContext(FallbackContext);
}

/**
 * The public zone's frame: gov-bar, header, the fallback notice, the page, the footer.
 *
 * Replaces `layout/base.html`. What that file did and this does not:
 *
 * - `<title>` per page. The template built it per request from a controller attribute. Nothing
 *   replaced that, so every public route inherited index.html's `AKIS — Cabinet` — the public
 *   site claiming to be the cabinet, on every page. The zone-level title below is the floor; a
 *   page that wants its own can call useDocumentTitle after this.
 * - The stylesheet and font <link>s, which Vite emits into the shell it builds.
 * - The Leaflet stylesheet, loaded in <head> only for /harta. That page can import it directly,
 *   which keeps it off every other page's critical path instead of on one page's.
 */
export function PublicShell() {
  const { t } = useTranslation(publicLayout);
  const [pageTitle, setPageTitle] = useState<string | null>(null);
  useDocumentTitle(publicDocumentTitle(pageTitle, t('titleSuffix'), t('appTitle')));
  // Client-side navigation never reaches the server, and behind nginx neither does the first
  // page — FR-038's per-page counting depends on the shell reporting every page. The cabinet
  // reports its own, to its own endpoint (PAGE_VIEW).
  usePageViewCounter(PAGE_VIEW.public);
  const [fallback, setFallback] = useState<LanguageFallback>(LANGUAGE_FALLBACK.none);
  const notice = FALLBACK_NOTICE[fallback];
  // Stable across renders so a page's effect that calls it does not re-fire every render.
  const setter = useMemo(() => setFallback, []);
  const titleSetter = useMemo(() => setPageTitle, []);

  return (
    <PageTitleContext.Provider value={titleSetter}>
      <FallbackContext.Provider value={setter}>
        <a className="skip-link" href="#main">
          {/* The template labelled this with nav.home, so it announced itself as "Acasă" —
            a skip link that says "Home" tells a screen-reader user nothing about where it
            goes. Corrected here to the string the cabinet already uses. */}
          {t('skipLink')}
        </a>

        <GovBar
          contained
          links={
            <>
              <Link to={SITE_PAGE.accessibility}>{t('govbarAccessibility')}</Link>
              <Link to="/sitemap">{t('navSitemap')}</Link>
            </>
          }
        />

        {/* Zero-height marker the sticky-header CSS observes to know when the header has left the
          top of the page. Carried across because the shadow-on-scroll depends on it. */}
        <div className="header-sentinel" aria-hidden="true" />
        <PublicHeader />

        {notice && (
          <div className="lang-fallback" role="status">
            {t(notice)}
          </div>
        )}

        <main id="main" className="site-main">
          <Suspense fallback={<RouteFallback />}>
            <Outlet />
          </Suspense>
        </main>

        <SiteFooter />

        {/* AppShell and FlowShell have had this since they were written; the public zone never did,
          because until the Thymeleaf migration every navigation here was a full page load and the
          browser reset the scroll itself. With client-side routing it does not: clicking a policy
          link in the footer of a 6700px home page changed the route and left the reader looking at
          the footer of the new one, which reads as "the link is broken". */}
        <ScrollRestoration />
      </FallbackContext.Provider>
    </PageTitleContext.Provider>
  );
}
